import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { basicService, groupsService } from '@app/services/';
import {
    handleGetGroupData,
    handleGetApplicationsInGroupData,
    handleGetVaultsInGroupData,
    handleGetPeopleCountInGroup,
    handleGetGroupsCountInGroup,
    setExceptionHandler,
    handleDisconnectGroupFromDirsync,
    getModalSwitch,
    addDetailsBreadcrumb,
    removeDetailsBreadcrumb,
    handleAddGroupsToGroups
} from '@app/store/actions';
import { IGroup, IFabMenu, TranslationsTypes, IUserAttribute } from '@app/models';
import { emptyGuid, hasOrgPermission, usersCount, groupsCount, genericErrorToast, userAttributesToHide, createTranslation } from '@app/helpers';
import { Link } from '@material-ui/core';
import { ManageAccount, Administrator } from '@app/store/constants';
import CardCountEdit from './components/cardCountEdit.component';
import { FabMenu, IModalData, ModalComponent, PasslyButton } from '@app/components';
import InfoIcon from '@material-ui/icons/Info';
import { confirm } from '@app/components/Modal/confirmModal';
import AddEditGroupModalForm from '@app/pages/directoryManager/dialogs/addEditGroup.dialog';
import { AdUsersToGroupDialog } from '@app/pages/directoryManager/dialogs/addUsersToGroup.dialog';
import { RemoveGroupsFromGroupDialog } from '@app/pages/directoryManager/dialogs/removeGroupsFromGroup.dialog';
import { RemoveUsersFromGroupDialog } from '@app/pages/directoryManager/dialogs/removeUsersFromGroup.dialog';
import { FormMode } from '@app/helpers/enums';
import { useParams } from 'react-router';
import { AddGroups } from '../dialogs';
import { PasslyTooltip } from '@app/componentsV2';

const GroupDetailsPage = () => {
    const { id: groupId } = useParams<{ id: string }>();

    let thisGroup: IGroup = useSelector((state: any) => state.groups.group);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const vaultsInGroup: [] = useSelector((state: any) => state.groups.vaultsInGroup);
    const applicationsInGroup: [] = useSelector((state: any) => state.groups.applicationsInGroup);
    const peopleInGroupCount: number = useSelector((state: any) => state.groups.peopleInGroupCount);
    const groupsInGroupCount: number = useSelector((state: any) => state.groups.groupsInGroupCount);
    const shellConfig: any = useSelector((state: any) => state.basic.shellConfig);

    const [dirsyncGroup, setDirsyncGroup] = useState(false);
    const [office365Group, setOffice365Group] = useState(false);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    useEffect(() => {
        dispatch(setExceptionHandler(true));
        dispatch(handleGetGroupData(groupId));
        dispatch(handleGetApplicationsInGroupData(groupId));
        dispatch(handleGetVaultsInGroupData(groupId));
        dispatch(handleGetGroupsCountInGroup(groupId));
        dispatch(handleGetPeopleCountInGroup(groupId));
        dispatch(getModalSwitch(false));

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    useEffect(() => {
        isDirsyncGroup();
    }, [thisGroup.SyncSource]);

    useEffect(() => {
        isOffice365Group();
    }, [thisGroup.ApplicationSyncSource]);

    useEffect(() => {
        dispatch(addDetailsBreadcrumb(thisGroup.Name));
    }, [thisGroup.Name]);

    const isDirsyncGroup = () => {
        if (thisGroup.SyncSource && thisGroup.SyncSource !== emptyGuid) {
            setDirsyncGroup(true);
        } else {
            setDirsyncGroup(false);
        }
    };

    const isOffice365Group = () => {
        if (thisGroup.ApplicationSyncSource && thisGroup.ApplicationSyncSource !== emptyGuid) {
            setOffice365Group(true);
        } else {
            setOffice365Group(false);
        }
    };

    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const resolveOrgName = (item: any) => {
        if (item && item.OrganizationId) {
            const orgs: any = shellConfig.organizations;

            for (let org of orgs) {
                if (org.id === item.OrganizationId) {
                    return org.name;
                }
            }
        }

        return null;
    };

    const detectGroupListAction: Function = () => {
        if (hasOrgPermission([ManageAccount]) && !dirsyncGroup) {
            return groupsEditableList();
        } else {
            return groupsReadonlyList();
        }
    };

    const detectUserListAction: Function = () => {
        if (hasOrgPermission([ManageAccount]) && !dirsyncGroup) {
            return usersEditableList();
        } else {
            return usersReadonlyList();
        }
    };

    const usersEditableList = () => {
        RemoveUsersFromGroup(false);
    };

    const usersReadonlyList = () => {
        RemoveUsersFromGroup(true);
    };

    const groupsEditableList = () => {
        removeGroupsFromGroup(false);
    };

    const groupsReadonlyList = () => {
        removeGroupsFromGroup(true);
    };

    const editName = () => {
        dispatch(getModalSwitch(true));

        setModalData({
            headerTitle: translation('EDIT_GROUP'),
            modalSwitcher: modalSwitchState,
            form: <AddEditGroupModalForm formMode={FormMode.Edit} handleRefresh={handlePageRefreshFromDialog} groupBeingEdited={thisGroup} />
        });
    };

    const handlePageRefreshFromDialog = (newGroupName: TranslationsTypes) => {
        thisGroup.Name = newGroupName;
    };

    const menu: IFabMenu = {
        name: 'ADD_PERMISSIONS',
        actions: [
            {
                name: 'ADD_GROUP',
                method: () => addGroupsToGroup(),
                iconElement: <i className='icon icon-user-groups' />
            },
            {
                name: 'ADD_USER',
                method: () => addUserToGroup(),
                iconElement: <i className='icon icon-users' />
            }
        ]
    };

    const RemoveUsersFromGroup = (disableEdit: boolean) => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: disableEdit ? translation('USERS_IN_GROUP') : translation('REMOVE_USERS_FROM_GROUP'),
            modalSwitcher: modalSwitchState,
            form: <RemoveUsersFromGroupDialog disableEdit={disableEdit} />
        });
    };

    const removeGroupsFromGroup = (disableEdit: boolean) => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: disableEdit ? translation('GROUPS_IN_GROUP') : translation('REMOVE_GROUPS_FROM_GROUP'),
            modalSwitcher: modalSwitchState,
            form: <RemoveGroupsFromGroupDialog disableEdit={disableEdit} />
        });
    };

    const addUserToGroup = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_USERS_TO_GROUP'),
            modalSwitcher: modalSwitchState,
            form: <AdUsersToGroupDialog />
        });
    };

    const addGroupsToGroup = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_GROUPS_TO_GROUP'),
            modalSwitcher: modalSwitchState,
            form: (
                <AddGroups
                    dataFunction={groupsService.getGroupsNotInGroup}
                    requestFunction={handleAddGroupsToGroups}
                    noDataLabel={'NO_GROUPS_TO_ADD_TO_GROUP'}
                />
            )
        });
    };

    const disconnectGroup = () => {
        confirm(
            translation('DESYNC_GROUP_CONFIRM_BODY'),
            translation('DESYNC_GROUP'),
            translation('CANCEL'),
            translation('DESYNC_GROUP_CONFIRM')
        ).then(
            () => {
                handleDisconnectGroupFromDirsync(groupId, genericErrorToast, handleDisconnectSuccess);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const handleDisconnectSuccess = () => {
        thisGroup.SyncSource = emptyGuid;
        setDirsyncGroup(false);
    };

    const translateAttributeName = (attributeKey: string) => {
        const name: TranslationsTypes = createTranslation('KNOWN_ATTRIBUTE_' + attributeKey.replace('.', '_').toUpperCase());

        const translated: string = translation(name);

        if (translated === name) {
            return attributeKey;
        }

        return translated;
    };

    return (
        <>
            <ModalComponent {...modalData} />

            <div className='content--narrow detailPage'>
                <div className='detailPage__section'>
                    <div className='detailPage__section-head'>{translation('GROUPS_DETAILS')}</div>

                    <div className='detailPage__section-content'>
                        {dirsyncGroup && (
                            <div className='detailPage__notification'>
                                <span className='detailPage__notification--info_icon'>
                                    <InfoIcon color='action' />
                                </span>
                                <span>{translation('ADD_GROUP_PERMISSIONS_WARNING')}</span>
                            </div>
                        )}
                        {office365Group && (
                            <div className='detailPage__notification'>
                                <span className='detailPage__notification--info_icon'>
                                    <InfoIcon color='action' />
                                </span>
                                <span>{translation('OFFICE_365_SYNC')}</span>
                            </div>
                        )}
                        <div className='rowcontainer'>
                            <ul className='detailPage__list'>
                                {hasOrgPermission([ManageAccount]) && !dirsyncGroup && (
                                    <li>
                                        <Link id='editPrimary' className='detailPage__list--primary-label' onClick={() => editName()}>
                                            {translation('EDIT')}
                                        </Link>
                                    </li>
                                )}
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('GROUPS_NAME')}</span>
                                    <span className='detailPage__list--secondary-label'>{thisGroup.Name}</span>
                                </li>
                            </ul>
                            {peopleInGroupCount > 0 && (
                                <CardCountEdit
                                    icon='icon-users'
                                    bottomLabel={translation(usersCount(peopleInGroupCount))}
                                    count={peopleInGroupCount}
                                    action={() => detectUserListAction()}
                                    synced={dirsyncGroup}
                                />
                            )}
                            {groupsInGroupCount > 0 && (
                                <CardCountEdit
                                    icon='icon-user-groups'
                                    bottomLabel={translation(groupsCount(groupsInGroupCount))}
                                    count={groupsInGroupCount}
                                    action={() => detectGroupListAction()}
                                    synced={dirsyncGroup}
                                />
                            )}
                        </div>
                        {hasOrgPermission([Administrator]) && dirsyncGroup && (
                            <div className='detailPage__button_container'>
                                <PasslyButton id='desyncGroupButton' variant='contained' color='primary' onClick={() => disconnectGroup()}>
                                    {translation('DESYNC_GROUP')}
                                </PasslyButton>
                            </div>
                        )}
                    </div>
                </div>

                <div className='detailPage__section'>
                    <div className='detailPage__section-head'>{translation('ACCOUNT_ATTRIBUTES')}</div>
                    {!thisGroup.Attributes || thisGroup.Attributes.length === 0 ? (
                        <div className='detailPage__section-content'>
                            <p>{translation('EMPTY_USER_ATTR_LIST')}</p>
                        </div>
                    ) : (
                        <div className='detailPage__section-content'>
                            <ul className='detailPage__list'>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('ATTRIBUTE_NAME')}</span>
                                    <span className='detailPage__list--secondary-label'>{translation('ATTRIBUTE_VALUE')}</span>
                                </li>
                                {thisGroup.Attributes.map((item: IUserAttribute, index: number) => (
                                    <>
                                        {!userAttributesToHide.includes(item.Key) && (
                                            <li key={index}>
                                                <span className='detailPage__list--primary-label'>{translateAttributeName(item.Key)}</span>
                                                <span className='detailPage__list--secondary-label'>{item.Value}&nbsp;&nbsp;</span>
                                            </li>
                                        )}
                                    </>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {basicService.isDefaultOrgBeta() && basicService.orgFeaturePasswordServerEnabled() && (
                    <div className='detailPage__section'>
                        <div className='detailPage__section-head'>{translation('VAULTS_DETAILS')}</div>

                        <div className='detailPage__section-content'>
                            <ul className='detailPage__list'>
                                {vaultsInGroup.length > 0 ? (
                                    <>
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('VAULTS')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                <ul className='detailPage__secondary-list'>
                                                    {vaultsInGroup.map((item: any, index: number) => (
                                                        <li key={index}>
                                                            <span>{item.DisplayName}&nbsp;</span>
                                                            <span className='detailPage__item-highlight'>({resolveOrgName(item)} - <PasslyTooltip title={item.Id}><span>{`...${item.Id.slice(-12)}`}</span></PasslyTooltip>)&nbsp;</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </span>
                                        </li>
                                    </>
                                ) : (
                                    <span>{translation('EMPTY_VAULT_FOR_GROUP')}</span>
                                )}
                            </ul>
                        </div>
                    </div>
                )}

                <div className='detailPage__section'>
                    <div className='detailPage__section-head'>{translation('APPLICATIONS_DETAILS')}</div>

                    <div className='detailPage__section-content'>
                        <ul className='detailPage__list'>
                            {applicationsInGroup.length ? (
                                <>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('APPLICATIONS')}</span>

                                        <span className='detailPage__list--secondary-label'>
                                            <ul className='detailPage__secondary-list'>
                                                {applicationsInGroup.map((item: any, index: number) => (
                                                    <li key={index}>
                                                        <span>{item.DisplayName}&nbsp;</span>
                                                        <span className='detailPage__item-highlight'>({resolveOrgName(item)})&nbsp;</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </span>
                                    </li>
                                </>
                            ) : (
                                <span>{translation('EMPTY_APPLICATION_FOR_GROUP')}</span>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            {!dirsyncGroup && hasOrgPermission([ManageAccount]) && <FabMenu id='addToGroupFab' menu={menu} />}
        </>
    );
};

export default GroupDetailsPage;
